import { useState, useMemo } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { Buffer } from "buffer";
import {
  Typography,
  Box,
  Button,
  Autocomplete,
  TextField,
  FormControl,
  Radio,
  RadioGroup,
  FormLabel,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { subDays } from "date-fns";

import StandardTable from "../tables/StandardTable";
import { capitalizeFirstLetter, convertAllDates, camelToNormal, downloadExcel, downloadPdf, dashToTitle } from "../../utils/function";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { ICall } from "../../types/callRail";
import { useAppDispatch } from "../../app/hooks";
import { IUser } from "../../types/user";
import { selectFilterProjects, setFilterProjects } from "../../features/project/projectSlice";
import CallFilter from "./CallFilter";
import { showErrorSnackbar, showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import { IProject } from "../../types/project";
import { IConnection } from "../../types/registrant";
import { Flex } from "../../commonStyles";
import { GlobalModal } from "../../features/modal/Modal";
import { handleModal } from "../../features/modal/modalSlice";

const Notifications = () => {
  const user = useSelector(selectUser);
  const filterProjects = useSelector(selectFilterProjects);
  const dispatch = useAppDispatch();
  if (filterProjects.length) dispatch(setFilterProjects([]));
  const storeDispatch = useAppDispatch();
  const [calls, setCalls] = useState<ICall[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [dateStart, setDateStart] = useState<Date | null>(new Date(subDays(new Date(), 1)));
  const [dateEnd, setDateEnd] = useState<Date | null>(new Date());
  const [answerStatus, setAnswerStatus] = useState<string>("");
  const [direction, setDirection] = useState<string>("");
  const [projects, setProjects] = useState<IProject | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<IUser>();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);
  const [callType, setCallType] = useState<string>("project");

  const [getTwilioCalls, { loading }] = useLazyQuery(GETPHONEDETAILS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setCalls(data.getTwilioData.calls);
      setTotalRecords(data.getTwilioData.calls.length);
    },
    onError: (e) => {
      storeDispatch(showErrorSnackbar(e.message));
      setCalls([]);
    },
  });

  const [callNumber] = useLazyQuery(CALLREGISTRANT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar("Call initiated successfully"));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(err.message));
    },
  });

  const [getVoicemail, { loading: loadingVoicemail }] = useLazyQuery(GETVOICEMAIL, {
    fetchPolicy: "network-only",
    onCompleted: async (data) => {
      const buffer: any = Buffer.from(data.getVoicemail, "base64");
      const audioBlob = await new Blob([buffer], { type: "audio/x-wav" });
      const blobUrl = URL.createObjectURL(audioBlob);

      const element = document.createElement("a");
      element.href = blobUrl;
      element.download = `Recording`;
      element.click();
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(err.message));
    },
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Project",
        accessor: (rowData: any) => (rowData.registrant ? rowData.registrant.project.name : "N/A"),
      },
      {
        Header: "RDS User",
        accessor: (rowData: any) => rowData.user,
      },
      {
        Header: "Registrant",
        accessor: (rowData: any) => {
          if (rowData.registrant) {
            return (
              <Link
                target="_blank"
                style={{ textDecoration: "none", color: "#000" }}
                to={`/dashboard/project/${rowData.registrant.project._id}/registrant/${rowData.registrant._id}`}
              >
                <strong>{rowData.registrant.fullName}</strong>
              </Link>
            );
          }
        },
      },
      {
        Header: "Registrant Phone Number",
        accessor: (rowData: any) => {
          if (rowData.direction === "inbound") {
            return rowData.from;
          } else return rowData.to;
        },
      },
      {
        Header: "Registrant Type",
        accessor: (rowData: any) => (rowData.registrant ? camelToNormal(rowData.registrant.realtorType) : ""),
      },
      {
        Header: "Date",
        accessor: (rowData: any) => convertAllDates(rowData.dateCreated, "PPpp"),
      },
      {
        Header: "Status",
        accessor: (rowData: any) => dashToTitle(rowData.status),
      },
      {
        Header: "Direction",
        accessor: (rowData: any) => capitalizeFirstLetter(rowData.direction),
      },
      {
        Header: "Duration(s)",
        accessor: (rowData: any) => rowData.duration,
      },
      {
        Header: "Recording",
        accessor: (rowData: any) => {
          return user?.type !== "Developer" && rowData.recording && rowData.direction === "inbound" ? (
            <Button
              disabled={loadingVoicemail ? true : false}
              variant="contained"
              onClick={() => getVoicemail({ variables: { user: user?._id, callId: rowData.id } })}
            >
              {loadingVoicemail ? <CircularProgress size={16} color="success" /> : rowData.recording}
            </Button>
          ) : null;
        },
      },
      {
        Header: "Call",
        accessor: (rowData: any) => {
          return (
            <Button
              variant="contained"
              onClick={() => {
                setSelectedProject(projects ? projects : null);
                setPhoneNumber(rowData.direction === "inbound" ? rowData.from.slice(2) : rowData.to.slice(2));
                storeDispatch(handleModal(true));
              }}
            >
              Call Back
            </Button>
          );
        },
      },
    ];
  }, [storeDispatch, user?.projectAccess, projects]);

  const download = async (type: string) => {
    let columns = [
      {
        label: "Project",
        id: "project",
      },
      {
        label: "RDS User",
        id: "user",
      },
      {
        label: "Registrant",
        id: "registrant",
      },
      {
        label: "Registrant Phone Number",
        id: "registrantPhone",
      },
      {
        label: "registrantType",
        id: "registrantType",
      },
      {
        label: "Date",
        id: "date",
      },
      {
        label: "Status",
        id: "status",
      },
      {
        label: "Direction",
        id: "direction",
      },
      {
        label: "Duration",
        id: "duration",
      },
      {
        label: "Connected",
        id: "connected",
      },
      {
        label: "Connected Date",
        id: "connectedDate",
      },
    ];
    let widths: any = {
      project: 15,
      user: 15,
      registrant: 15,
      registrantPhone: 15,
      registrantType: 15,
      date: 15,
      answered: 15,
      direction: 15,
      duration: 15,
      connected: 15,
      connectedDate: 15,
    };
    let pdfWidths: any = {
      project: 200,
      user: 200,
      registrant: 200,
      registrantPhone: 200,
      registrantType: 200,
      date: 200,
      status: 200,
      direction: 200,
      duration: 200,
      connected: 200,
      connectedDate: 200,
    };
    let sheetTitle = "CRM Phone Summary";
    let allData = calls.map((data: any) => {
      let connected;
      if (data.registrant && data.registrant.connections && dateStart && dateEnd) {
        connected = data.registrant.connections.find(
          (connection: IConnection) =>
            new Date(dateStart?.setHours(0, 0, 0, 0)).valueOf() < new Date(connection.date).valueOf() &&
            new Date(connection.date).valueOf() < dateEnd?.valueOf()
        );
      }
      return {
        project: data.registrant ? data.registrant.project.name : "N/A",
        user: data.user ? data.user : "",
        registrant: data.registrant ? data.registrant.fullName : "",
        registrantPhone: data.direction === "inbound" ? data.from : data.to,
        registrantType: data.registrant ? camelToNormal(data.registrant.realtorType) : "",
        date: convertAllDates(data.dateCreated, "PPpp"),
        status: dashToTitle(data.status),
        direction: data.direction,
        duration: data.duration,
        connected: connected ? "Yes" : "No",
        connectedDate: connected ? convertAllDates(new Date(connected.date), "PPpp") : "",
      };
    });
    if (type === "excel") {
      downloadExcel([allData], [columns], [], [[widths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([allData], [columns], [], [pdfWidths], [sheetTitle], sheetTitle);
    }
  };

  return (
    <Box>
      <GlobalModal>
        <Typography variant="h2" sx={{ mb: 2 }}>
          <strong>{selectedProject ? `Calling about ${selectedProject.name}` : "Call a Number"}</strong>
        </Typography>
        <Autocomplete
          options={[user?.projectAccess!.map((projectAccess: any) => projectAccess?.project)].flat()}
          freeSolo={false}
          value={selectedProject}
          isOptionEqualToValue={(option, value) => option === value}
          getOptionLabel={(option: any) => option.name}
          onChange={(e, value) => {
            setSelectedProject(value);
          }}
          renderInput={(params) => <TextField {...params} label="Which project are you calling about?" required />}
        />
        <TextField
          sx={{ my: 2 }}
          title={"Phone Number"}
          name={"firstName"}
          type="number"
          fullWidth
          required
          value={phoneNumber}
          label={"Phone Number"}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPhoneNumber(e.target.value);
          }}
        />
        {user?.trackingPhone ? (
          <FormControl>
            <FormLabel id="demo-radio-buttons-group-label">Which number would you like call with?</FormLabel>
            <RadioGroup
              value={callType}
              onChange={(e) => setCallType(e.target.value)}
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="project"
              name="radio-buttons-group"
            >
              <FormControlLabel value="project" control={<Radio />} label="Project Number" />
              <FormControlLabel value="user" control={<Radio />} label="Personal Number" />
            </RadioGroup>
          </FormControl>
        ) : null}
        <Box>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              if (phoneNumber.length !== 10) {
                return storeDispatch(showErrorSnackbar("Please enter a valid 10 digit phone number"));
              }
              let query: any = {
                customerPhone: parseInt(phoneNumber, 10),
                user: user?._id,
                project: selectedProject?._id,
                callType: callType,
              };
              callNumber({
                variables: query,
              });
            }}
          >
            Call
          </Button>
        </Box>
      </GlobalModal>
      <Typography variant="h2" component="div" gutterBottom>
        <strong>{projects ? `${projects.name} ` : selectedUser ? `${selectedUser.fullName} ` : ""}Phone Calls</strong>
      </Typography>
      <CallFilter
        dateStart={dateStart}
        setDateStart={setDateStart}
        dateEnd={dateEnd}
        setDateEnd={setDateEnd}
        answerStatus={answerStatus}
        setAnswerStatus={setAnswerStatus}
        direction={direction}
        setDirection={setDirection}
        projects={projects}
        setProjects={setProjects}
        setPageNumber={setPageNumber}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
      />
      {user?.type !== "Developer" ? (
        <Flex sx={{ mt: 2 }}>
          <Button
            sx={{ mt: { xs: 2, md: 0 } }}
            color="primary"
            variant="contained"
            onClick={() => {
              if (user?.type !== "Developer") {
                let query: any = {
                  dateStart,
                  dateEnd,
                  calls: true,
                  limit: 1000,
                };
                if (projects) {
                  query.projects = projects ? [projects._id] : [];
                }
                if (!projects) {
                  query.user = selectedUser ? selectedUser._id : user?._id;
                }
                if (direction) {
                  query.direction = direction;
                }
                getTwilioCalls({
                  variables: query,
                });
              }
            }}
          >
            Search
          </Button>
          <Button sx={{ mt: { xs: 2, md: 0 }, ml: 2 }} color="success" variant="contained" onClick={() => storeDispatch(handleModal(true))}>
            Call
          </Button>
        </Flex>
      ) : null}
      <Box sx={{ mt: 2 }}>
        <StandardTable
          data={calls.slice(pageNumber * 30, 30 * (pageNumber + 1))}
          columns={columns}
          loading={loading}
          download={download}
          user={user}
        />
      </Box>
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Button
          disabled={pageNumber === 0}
          onClick={() => {
            setPageNumber((pageNumber) => pageNumber - 1);
          }}
        >
          {"<"}
        </Button>
        <span>{pageNumber + 1}</span>
        <Button
          disabled={pageNumber + 1 === Math.ceil(totalRecords / 30) || !totalRecords}
          onClick={() => {
            setPageNumber((pageNumber) => pageNumber + 1);
          }}
        >
          {">"}
        </Button>
      </Box>
    </Box>
  );
};

const GETPHONEDETAILS = gql`
  query getTwilioData($projects: [MongoID], $dateStart: Date, $dateEnd: Date, $calls: Boolean, $user: MongoID, $direction: String, $limit: Int) {
    getTwilioData(projects: $projects, dateStart: $dateStart, dateEnd: $dateEnd, calls: $calls, user: $user, direction: $direction, limit: $limit) {
      calls {
        id
        dateCreated
        duration
        direction
        status
        to
        from
        forwardedFrom
        phone
        user
        recording
        registrant {
          project {
            name
            _id
          }
          _id
          fullName
          realtorType
          connections {
            date
          }
        }
      }
    }
  }
`;

const CALLREGISTRANT = gql`
  query callRegistrant($project: MongoID, $customerPhone: Float!, $user: MongoID, $callType: String!) {
    callRegistrant(project: $project, customerPhone: $customerPhone, user: $user, callType: $callType)
  }
`;

const GETVOICEMAIL = gql`
  query getVoicemail($user: MongoID, $callId: String!) {
    getVoicemail(user: $user, callId: $callId)
  }
`;

export default Notifications;
